import React, { useEffect, useState } from 'react';
import { X, Loader2, Hash, Type, Tag } from 'lucide-react';
import { api } from '@/lib/api';

/**
 * Field-stats popup for the right-click "View Data" affordance on a field.
 * - Dimensions: top-N distinct values + counts, total-distinct + null-count footer.
 * - Measures: numeric summary (min, max, avg, sum, count, distinct, null).
 * - Calculated measures: shows the formula + a hint to add to a visual.
 *
 * Renders nothing when `field` is null. Self-fetches stats whenever the
 * (modelId, field) pair changes.
 */
export default function FieldStatsModal({ modelId, field, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!field || !modelId) return;
    setData(null);
    setError(null);
    setLoading(true);
    api.getFieldStats(modelId, field)
      .then(setData)
      .catch(err => setError(err.message || 'Failed to load stats'))
      .finally(() => setLoading(false));
  }, [modelId, field]);

  // Close on Escape.
  useEffect(() => {
    if (!field) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [field, onClose]);

  if (!field) return null;

  // Builders may pass an entity-qualified id ("ent_xxx::COLUMN"); show the plain
  // column name in the header (the backend resolves the qualified form itself).
  const baseField = typeof field === 'string' && field.includes('::') ? field.split('::').pop() : field;

  const fmt = (n) => {
    if (n === null || n === undefined) return '—';
    if (typeof n !== 'number') return String(n);
    if (Number.isInteger(n)) return n.toLocaleString();
    return n.toLocaleString(undefined, { maximumFractionDigits: 4 });
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose} data-testid="field-stats-modal">
      <div className="glass-card rounded-2xl border border-border/50 shadow-2xl w-[520px] max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between gap-3 p-4 border-b border-border/30">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-muted-foreground">
              {data?.kind === 'measure' ? <Hash className="w-3 h-3" /> : data?.kind === 'calculated_measure' ? <Hash className="w-3 h-3" /> : <Tag className="w-3 h-3" />}
              <span>{data?.kind === 'calculated_measure' ? 'Calculated measure' : (data?.kind || (loading ? 'Loading…' : 'Field'))}</span>
              {data?.data_type && <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted/40 border border-border/30 normal-case tracking-normal flex items-center gap-1"><Type className="w-2.5 h-2.5" />{data.data_type}</span>}
            </div>
            <h3 className="text-base font-heading font-semibold text-foreground mt-1 truncate" data-testid="field-stats-title">{data?.business_name || baseField}</h3>
            <p className="text-[11px] text-muted-foreground font-mono mt-0.5 truncate">{baseField}</p>
            {data?.description && <p className="text-xs text-foreground/70 mt-1.5 leading-relaxed">{data.description}</p>}
          </div>
          <button onClick={onClose} className="rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 p-1 flex-shrink-0" aria-label="Close" data-testid="field-stats-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto" data-testid="field-stats-body">
          {loading && (
            <div className="flex items-center justify-center p-8 text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              <span className="text-xs">Computing stats…</span>
            </div>
          )}
          {error && !loading && (
            <div className="p-4 text-xs text-rose-400">{error}</div>
          )}

          {data?.kind === 'measure' && (
            <div className="grid grid-cols-2 gap-2 p-4" data-testid="measure-stats">
              <Stat label="Min" value={fmt(data.min)} />
              <Stat label="Max" value={fmt(data.max)} />
              <Stat label="Average" value={fmt(data.avg)} />
              <Stat label="Sum" value={fmt(data.sum)} />
              <Stat label="Count" value={fmt(data.count)} />
              <Stat label="Distinct" value={fmt(data.distinct_count)} />
              <Stat label="Null/Missing" value={fmt(data.null_count)} />
              <Stat label="Total rows" value={fmt(data.total_rows)} />
            </div>
          )}

          {data?.kind === 'calculated_measure' && (
            <div className="p-4 space-y-3">
              <div className="text-xs text-foreground/80">{data.message}</div>
              {data.formula && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Formula</div>
                  <pre className="text-xs font-mono bg-muted/30 border border-border/30 rounded-lg p-2.5 whitespace-pre-wrap break-words">{data.formula}</pre>
                </div>
              )}
            </div>
          )}

          {data?.kind === 'dimension' && (
            <div className="p-4">
              <div className="grid grid-cols-3 gap-2 mb-3">
                <Stat label="Total distinct" value={fmt(data.total_distinct)} />
                <Stat label="Null/Missing" value={fmt(data.null_count)} />
                <Stat label="Total rows" value={fmt(data.total_rows)} />
              </div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2 flex items-center justify-between">
                <span>Top values</span>
                {data.sample_capped && <span className="normal-case tracking-normal text-amber-400">showing {data.distinct_values.length} of {data.total_distinct}</span>}
              </div>
              <div className="border border-border/30 rounded-lg overflow-hidden" data-testid="distinct-values-table">
                <table className="w-full text-xs">
                  <tbody>
                    {data.distinct_values.length === 0 ? (
                      <tr><td className="p-3 text-center text-muted-foreground">No values</td></tr>
                    ) : data.distinct_values.map((row, i) => {
                      const pct = data.total_rows > 0 ? Math.round((row.count / data.total_rows) * 100) : 0;
                      return (
                        <tr key={i} className="border-b border-border/20 last:border-b-0 hover:bg-muted/20 transition-colors">
                          <td className="p-2 max-w-[260px] truncate text-foreground" title={String(row.value)}>{String(row.value)}</td>
                          <td className="p-2 w-20 text-right text-foreground/80 tabular-nums">{fmt(row.count)}</td>
                          <td className="p-2 w-12 text-right text-muted-foreground tabular-nums">{pct}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg border border-border/30 bg-muted/10 p-2.5">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-sm font-semibold text-foreground mt-0.5 tabular-nums truncate" title={value}>{value}</div>
    </div>
  );
}
